import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { Plus, Trash2, Pencil, QrCode, ImageIcon, Info, Users, MonitorPlay, ScanFace, ChevronRight, Lock } from 'lucide-react';
import { Album } from '../../types';
import { albumsApi } from '../../api/albums';
import { photosApi } from '../../api/photos';
import { MachhaCharacter } from '../../lib/machhaAsset';
import { Button } from '../UI/Button';
import { Modal } from '../UI/Modal';
import { AlbumCardSkeleton } from '../UI/Skeleton';
import { AlbumWizard } from './AlbumWizard';
import { AlbumEditModal } from './AlbumEditModal';
import { AlbumPhotosPreviewModal } from './AlbumPhotosPreviewModal';
import { AlbumDetailsModal } from './AlbumDetailsModal';
import { AlbumTypeChoiceDialog } from './AlbumTypeChoiceDialog';
import { LiveAlbumWizard } from './LiveAlbumWizard';
import { LiveBadge } from '../UI/LiveBadge';
import { SlideshowSetupModal } from './SlideshowSetupModal';
import { LiveAlbumStats } from './LiveAlbumStats';
import { FaceIndexStatus } from './FaceIndexStatus';
import { NonLiveProcessingStatus } from './NonLiveProcessingStatus';
import { EnableFaceRecognitionModal } from './EnableFaceRecognitionModal';
import { ShareAlbumModal } from './ShareAlbumModal';
import { clayStaggerContainer, clayStaggerItem } from '../../constants/animations';
import { track, EVENTS } from '../../lib/mixpanel';
import { useAuthStore } from '../../store/authStore';
import styles from './AlbumManagementPanel.module.css';
import enableFaceStyles from './EnableFaceMatchCard.module.css';

interface AlbumManagementPanelProps {
  eventId: string;
  /** Printed on the guest poster's header. */
  eventName?: string;
  eventStatus?: 'active' | 'ended';
  isEventCreator: boolean;
  canCreateAlbum: boolean;
}

// ─── Album card ──────────────────────────────────────────────────────────────

interface AlbumCardProps {
  album: Album;
  eventName?: string;
  eventStatus?: 'active' | 'ended';
  canEdit: boolean;
  onEdit: () => void;
  onPreview: () => void;
  onDetails: () => void;
  onDeleted: (albumId: string) => void;
  onSlideshowSetup?: () => void;
  onEnableFace: () => void;
}

function AlbumCard({ album, eventName, eventStatus, canEdit, onEdit, onPreview, onDetails, onDeleted, onSlideshowSetup, onEnableFace }: AlbumCardProps) {
  const showLiveBadge = album.isLive && eventStatus !== 'ended';
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const photoCount = album.photoCount ?? album.processedPhotoCount ?? 0;
  const memberCount = album.memberCount ?? 0;
  const hasCover = !!album.coverPhotoId;

  const stop = (fn: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    fn();
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await albumsApi.delete(album._id);
      track(EVENTS.ALBUM_DELETED, { album_id: album._id, album_name: album.name });
      onDeleted(album._id);
      toast.success('Album deleted');
    } catch {
      toast.error('Failed to delete album');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <motion.div
        variants={clayStaggerItem}
        className={styles['album-card']}
        whileHover={{ y: -3 }}
        whileTap={{ scale: 0.98 }}
        transition={{ type: 'spring', stiffness: 400, damping: 25 }}
        onClick={onPreview}
      >
        {/* Thumbnail area */}
        <div className={styles['thumbnail-area']}>
          {hasCover ? (
            <img
              src={photosApi.thumbnailUrl(album.coverPhotoId!)}
              alt={album.name}
              className={styles['thumbnail-img']}
            />
          ) : (
            <div className={styles['thumbnail-placeholder']}>
              <ImageIcon className={`${styles['thumbnail-placeholder-icon']} ${styles['icon-2xl']}`} />
            </div>
          )}

          {/* Badges */}
          <div className={styles['badges-overlay']}>
            {photoCount > 0 && (
              <div className={styles['count-badge']}>
                <ImageIcon className={styles['icon-xs']} />
                {photoCount}
              </div>
            )}
            {memberCount > 0 && (
              <div className={styles['count-badge']}>
                <Users className={styles['icon-xs']} />
                {memberCount}
              </div>
            )}
          </div>

          {/* Live indicator in thumbnail corner — hidden once the event ends */}
          {showLiveBadge && (
            <div className={styles['live-indicator']}>
              <LiveBadge active />
            </div>
          )}
        </div>

        {/* Card body */}
        <div className={styles['card-body']}>
          <div className={styles['card-title-row']}>
            <h3 className={styles['card-title']}>
              {album.name}
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {album.editAccess === 'creator_only' && (
                <span title="Only the album creator can edit this album" style={{ display: 'flex' }}>
                  <Lock style={{ width: 12, height: 12, opacity: 0.5, flexShrink: 0 }} />
                </span>
              )}
              {showLiveBadge && <LiveBadge active />}
            </div>
          </div>
          <p className={styles['card-description']}>
            {album.description || '\u00A0'}
          </p>

          {/* Real-time live album stats */}
          {album.isLive && (
            <LiveAlbumStats albumId={album._id} albumCreatedAt={album.createdAt} />
          )}

          {/* Overlay-compositing progress + retry button.
              The component self-hides when the album has no overlays.
              For live albums, progress is shown by LiveAlbumStats above — only
              actionable states (failed, stuck, force-restart) are surfaced here. */}
          <NonLiveProcessingStatus albumId={album._id} isLive={album.isLive} />

          {/* Face indexing status — visible to team members when face recognition is enabled.
              "Enable" CTA only surfaces for users who can edit this album. */}
          {album.faceRecognitionEnabled ? (
            <FaceIndexStatus albumId={album._id} />
          ) : canEdit ? (
            <button
              type="button"
              onClick={stop(onEnableFace)}
              className={enableFaceStyles['block']}
              aria-label="Enable face match for this album"
            >
              <span className={enableFaceStyles['icon-wrap']} aria-hidden>
                <ScanFace className={enableFaceStyles['icon-sm']} />
              </span>
              <span className={enableFaceStyles['text-col']}>
                <span className={enableFaceStyles['label']}>Face match</span>
                <span className={enableFaceStyles['sublabel']}>
                  Let guests find their photos with a selfie
                </span>
              </span>
              <span className={enableFaceStyles['cta-pill']}>
                Enable
                <ChevronRight className={enableFaceStyles['icon-xs']} aria-hidden />
              </span>
            </button>
          ) : null}

          {/* Download ZIP build status. Renders "All photos download" for every
              album and, for restricted (matched_only face-rec) albums, a
              "Highlights download" block directly below it — both in the same
              block fashion as the face-match status above. */}

          {/* Bottom action bar — primary actions, destructive isolated on the right */}
          <div
            className={styles['action-bar']}
            onClick={(e) => e.stopPropagation()}
            role="toolbar"
            aria-label="Album actions"
          >
            <button
              type="button"
              onClick={stop(() => setShowShare(true))}
              className={styles['action-btn']}
              title="View QR code, invite link and poster"
              aria-label="View QR code, invite link and poster"
            >
              <QrCode className={styles['action-btn-icon']} />
              <span className={styles['action-btn-label']}>QR &amp; Link</span>
            </button>

            {album.isLive && onSlideshowSetup && canEdit && (
              <button
                type="button"
                onClick={stop(onSlideshowSetup)}
                className={`${styles['action-btn']} ${album.slideshowEnabled ? styles['action-btn-active'] : ''}`}
                title={album.slideshowEnabled ? 'Manage venue display' : 'Set up venue display'}
                aria-label={album.slideshowEnabled ? 'Manage venue display' : 'Set up venue display'}
              >
                <MonitorPlay className={styles['action-btn-icon']} />
                <span className={styles['action-btn-label']}>Show</span>
                {album.slideshowEnabled && <span className={styles['action-btn-dot']} aria-hidden />}
              </button>
            )}

            <button
              type="button"
              onClick={stop(onDetails)}
              className={styles['action-btn']}
              title="View details"
              aria-label="View details"
            >
              <Info className={styles['action-btn-icon']} />
              <span className={styles['action-btn-label']}>Details</span>
            </button>

            {canEdit && (
              <>
                <button
                  type="button"
                  onClick={stop(onEdit)}
                  className={styles['action-btn']}
                  title="Edit album"
                  aria-label="Edit album"
                >
                  <Pencil className={styles['action-btn-icon']} />
                  <span className={styles['action-btn-label']}>Edit</span>
                </button>

                <div className={styles['action-bar-divider']} aria-hidden />

                <button
                  type="button"
                  onClick={stop(() => setShowDeleteConfirm(true))}
                  className={`${styles['action-btn']} ${styles['action-btn-danger']}`}
                  title="Delete album"
                  aria-label="Delete album"
                >
                  <Trash2 className={styles['action-btn-icon']} />
                  <span className={styles['action-btn-label']}>Delete</span>
                </button>
              </>
            )}
          </div>
        </div>
      </motion.div>

      {/* Delete confirmation */}
      <Modal isOpen={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} title="Delete album?" size="sm">
        <div className={styles['delete-modal-body']}>
          <p className={styles['delete-text']}>
            Deleting{' '}
            <strong className={styles['delete-album-name']}>{album.name}</strong>{' '}
            will remove all {album.isLive ? 'auto-added' : 'curated'} photos and member access.
          </p>
          <p className={styles['delete-warning']}>
            This cannot be undone.
          </p>
          <div className={styles['delete-actions']}>
            <Button variant="ghost" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
            <Button variant="danger" loading={deleting} onClick={handleDelete}>Delete Album</Button>
          </div>
        </div>
      </Modal>

      {/* QR code, invite link and printable poster */}
      <ShareAlbumModal
        isOpen={showShare}
        onClose={() => setShowShare(false)}
        albumId={album._id}
        albumName={album.name}
        eventName={eventName ?? ''}
        inviteToken={album.inviteToken}
      />
    </>
  );
}

// ─── Main panel ───────────────────────────────────────────────────────────────

export function AlbumManagementPanel({ eventId, eventName, eventStatus, isEventCreator, canCreateAlbum }: AlbumManagementPanelProps) {
  const { user } = useAuthStore();
  const [albums, setAlbums] = useState<Album[]>([]);
  const [loading, setLoading] = useState(true);
  const [showTypeChoice, setShowTypeChoice] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [showLiveWizard, setShowLiveWizard] = useState(false);
  const [editTarget, setEditTarget] = useState<Album | null>(null);
  const [previewTarget, setPreviewTarget] = useState<Album | null>(null);
  const [detailsTarget, setDetailsTarget] = useState<Album | null>(null);
  const [slideshowTarget, setSlideshowTarget] = useState<Album | null>(null);
  const [enableFaceTarget, setEnableFaceTarget] = useState<Album | null>(null);

  useEffect(() => {
    albumsApi
      .list(eventId)
      .then((r) => setAlbums(r.albums))
      .catch(() => toast.error('Failed to load albums'))
      .finally(() => setLoading(false));
  }, [eventId]);

  const handleCreated = (album: Album) => {
    setAlbums((prev) => [album, ...prev]);
  };

  const handleUpdated = (updated: Album) => {
    setAlbums((prev) => prev.map((a) => (a._id === updated._id ? updated : a)));
    setEditTarget((prev) => (prev?._id === updated._id ? updated : prev));
    setSlideshowTarget((prev) => (prev?._id === updated._id ? updated : prev));
  };

  const handleDeleted = (albumId: string) => {
    setAlbums((prev) => prev.filter((a) => a._id !== albumId));
  };

  const getCanEdit = (album: Album): boolean => {
    if (!user) return false;
    const createdById = typeof album.createdBy === 'object' ? album.createdBy._id : album.createdBy;
    if (createdById === user._id) return true;
    if (isEventCreator) return true;
    return album.editAccess !== 'creator_only';
  };

  return (
    <>
      {/* Content */}
      {loading ? (
        <div className={styles['albums-grid']}>
          {[0, 1, 2, 3].map((i) => <AlbumCardSkeleton key={i} />)}
        </div>
      ) : albums.length === 0 ? (
        <div className={styles['empty-state']}>
          <MachhaCharacter state="Empty" size={140} />
          <p className={styles['empty-title']}>
            No albums yet
          </p>
          <p className={styles['empty-description']}>
            {canCreateAlbum
              ? 'Create an album to share curated photos with guests using an invite link.'
              : 'Albums created for this event will appear here.'}
          </p>
          {canCreateAlbum && (
            <div className={styles['empty-button-wrapper']}>
              <Button onClick={() => setShowTypeChoice(true)}>
                <Plus className={styles['icon-sm']} />
                Create First Album
              </Button>
            </div>
          )}
        </div>
      ) : (
        <motion.div
          className={styles['albums-grid']}
          variants={clayStaggerContainer}
          initial="hidden"
          animate="visible"
        >
          {/* Add new album card — only shown to users who can create albums */}
          {canCreateAlbum && (
            <motion.button
              variants={clayStaggerItem}
              onClick={() => setShowTypeChoice(true)}
              className={styles['add-album-card']}
              whileHover={{ y: -3 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <div className={styles['add-album-icon-wrapper']}>
                <Plus className={`${styles['add-album-icon']} ${styles['icon-lg']}`} />
              </div>
              <span className={styles['add-album-label']}>
                New Album
              </span>
            </motion.button>
          )}

          {albums.map((album) => (
            <AlbumCard
              key={album._id}
              album={album}
              eventName={eventName}
              eventStatus={eventStatus}
              canEdit={getCanEdit(album)}
              onEdit={() => setEditTarget(album)}
              onPreview={() => setPreviewTarget(album)}
              onDetails={() => setDetailsTarget(album)}
              onDeleted={handleDeleted}
              onSlideshowSetup={album.isLive ? () => setSlideshowTarget(album) : undefined}
              onEnableFace={() => setEnableFaceTarget(album)}
            />
          ))}
        </motion.div>
      )}

      {/* Album type choice */}
      <AlbumTypeChoiceDialog
        isOpen={showTypeChoice}
        onClose={() => setShowTypeChoice(false)}
        onChoose={(type) => {
          setShowTypeChoice(false);
          if (type === 'live') setShowLiveWizard(true);
          else setShowWizard(true);
        }}
      />

      {/* Album creation wizard */}
      <AlbumWizard
        isOpen={showWizard}
        onClose={() => setShowWizard(false)}
        eventId={eventId}
        onCreated={handleCreated}
      />

      {/* Live album wizard */}
      <LiveAlbumWizard
        isOpen={showLiveWizard}
        onClose={() => setShowLiveWizard(false)}
        eventId={eventId}
        onCreated={handleCreated}
      />

      {/* Album edit modal */}
      <AlbumEditModal
        album={editTarget}
        eventId={eventId}
        isOpen={!!editTarget}
        onClose={() => setEditTarget(null)}
        onUpdated={handleUpdated}
      />

      {/* Album photos preview modal */}
      {previewTarget && (
        <AlbumPhotosPreviewModal
          album={previewTarget}
          isOpen={!!previewTarget}
          onClose={() => setPreviewTarget(null)}
        />
      )}

      {/* Album details modal */}
      <AlbumDetailsModal
        album={detailsTarget}
        isOpen={!!detailsTarget}
        onClose={() => setDetailsTarget(null)}
        eventStatus={eventStatus}
      />

      {/* Slideshow / Venue Display setup modal (live albums) */}
      <SlideshowSetupModal
        album={slideshowTarget}
        isOpen={!!slideshowTarget}
        onClose={() => setSlideshowTarget(null)}
        onUpdated={handleUpdated}
      />

      {/* Enable Face Recognition wizard — opens from the per-card CTA */}
      <EnableFaceRecognitionModal
        album={enableFaceTarget}
        isOpen={!!enableFaceTarget}
        onClose={() => setEnableFaceTarget(null)}
        onUpdated={handleUpdated}
      />
    </>
  );
}
